import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPatientsByIds } from "../../../../../api/healthcare/PatientApi";
import type { Patient } from "../../../../../types/healthcare/PatientTypes";
import type { SalesOrder } from "../../../../../types/inventory/OrderTypes";
import { canViewPatients } from "../../../../../utils/userPrefs/validatePermissions";

// Commerce holds only the patient uuid, so names come from healthcare under the viewer's own patients.read.
export const useSalesOrderAssignees = (orders: SalesOrder[]): { assignees: Map<string, Patient>; isLoading: boolean } => {
	const uuids = useMemo(
		() =>
			[
				...new Set(
					orders
						.filter((o) => !o.is_shopify_order && o.assignee_type === "user" && o.assignee_id)
						.map((o) => String(o.assignee_id).toLowerCase())
				),
			].sort(),
		[orders]
	);

	const { data, isLoading } = useQuery({
		queryKey: ["sales-order-assignees", uuids],
		queryFn: () => fetchPatientsByIds(uuids),
		enabled: canViewPatients() && uuids.length > 0,
	});

	const assignees = useMemo(
		() => new Map((data ?? []).map((p) => [String(p.id).toLowerCase(), p])),
		[data]
	);

	return { assignees, isLoading };
};

export default useSalesOrderAssignees;
